// useWellData — reactive localStorage CRUD for the well-spine, mirroring usePadData / usePadJobData.
//
// Bore→Well cutover Phase 2 (facade-over-spine): the backing store moved from a private
// useState(readStore()) to the shared, reactive `wellsStore` (see store/spineStore.ts). The PUBLIC
// API is unchanged, so consumers and the store-free tests are unaffected; the only behavior change is
// that a write now re-renders any component SUBSCRIBED to the reactive `wells` field (a snapshot
// captured via `getWells()` is unchanged). The useBorefieldData facade will consume this once its
// (Gate-A-gated) flip lands — not yet wired. RTK Query (wellApi) is still the "real" code path; this
// is the development fallback (CLAUDE.md API Strategy).

import { useCallback, useSyncExternalStore } from "react";
import { generateId, generatePrefixedId } from "../../../lib/id";
import { wellsStore, WELLS_STORAGE_KEY } from "../store/spineStore";
import type { Well, CreateWellDTO, Wellbore } from "../types";

/** Re-exported for tests/consumers that seed or assert the demo-persistence key directly. */
export { WELLS_STORAGE_KEY };

export interface UseWellDataReturn {
  wells: Well[];
  getWells: () => Well[];
  getWellById: (id: string) => Well | undefined;
  addWell: (well: Well) => void;
  createWell: (data: CreateWellDTO) => Well;
  updateWell: (well: Well) => void;
  archiveWell: (id: string) => void;
  restoreWell: (id: string) => void;
  deleteWell: (id: string) => void;
}

export function useWellData(): UseWellDataReturn {
  // Reactive read: re-renders on any wellsStore write (same-tab or cross-tab).
  const wells = useSyncExternalStore(
    wellsStore.subscribe,
    wellsStore.read,
    wellsStore.read,
  );

  const getWells = useCallback((): Well[] => wellsStore.read(), []);

  const getWellById = useCallback(
    (id: string): Well | undefined =>
      wellsStore.read().find((w) => w.id === id),
    [],
  );

  const addWell = useCallback((well: Well): void => {
    const current = wellsStore.read();
    const existingIndex = current.findIndex((w) => w.id === well.id);
    wellsStore.setAll(
      existingIndex >= 0
        ? current.map((w) => (w.id === well.id ? well : w))
        : [...current, well],
    );
  }, []);

  const createWell = useCallback(
    (data: CreateWellDTO): Well => {
      const now = new Date().toISOString();
      const id = generatePrefixedId("well");
      const { primaryWellbore, ...wellFields } = data;
      const wellbore: Wellbore = {
        ...primaryWellbore,
        // Bare generateId() (no prefix) so the wellbore id matches buildWell's convention and the
        // timeline's `wellboreId ← boreId` resolution — both paths mint the same id shape.
        id: generateId(),
        wellId: id,
        createdAt: now,
        modifiedAt: now,
      };
      const well: Well = {
        ...wellFields,
        id,
        wellbores: [wellbore],
        createdAt: now,
        modifiedAt: now,
      };
      addWell(well);
      return well;
    },
    [addWell],
  );

  const updateWell = useCallback((well: Well): void => {
    // Upsert by id: replace the matching well, refreshing modifiedAt. Mirrors usePadData.updatePad —
    // leaves the rest of the store untouched.
    wellsStore.setAll(
      wellsStore
        .read()
        .map((w) =>
          w.id === well.id
            ? { ...well, modifiedAt: new Date().toISOString() }
            : w,
        ),
    );
  }, []);

  // Archive = soft-delete (recoverable), never hard-delete. Stamps archivedAt + bumps modifiedAt
  // (an archive is an audit-relevant state transition — C449 §18.5). Nothing is removed from the
  // store; the well keeps all its wellbores/jobs/data so restore brings it back intact.
  // BACKEND: also stamp Wells.ArchivedBy/ModifiedBy when auth context exists.
  const archiveWell = useCallback((id: string): void => {
    const now = new Date().toISOString();
    wellsStore.setAll(
      wellsStore
        .read()
        .map((w) =>
          w.id === id ? { ...w, archivedAt: now, modifiedAt: now } : w,
        ),
    );
  }, []);

  // Restore = clear archivedAt. Uses `delete` on the optional field (never assigns undefined —
  // exactOptionalPropertyTypes) so a restored well is indistinguishable from a never-archived one.
  const restoreWell = useCallback((id: string): void => {
    const now = new Date().toISOString();
    wellsStore.setAll(
      wellsStore.read().map((w) => {
        if (w.id !== id) return w;
        const restored = { ...w, modifiedAt: now };
        delete restored.archivedAt;
        return restored;
      }),
    );
  }, []);

  // HARD DELETE — NOT the UI path. Per the no-hard-delete principle (PO 2026-06-30), the UI archives
  // (archiveWell); this destructive method is retained only for a future authorized admin/purge flow.
  // Do not wire it to a user action.
  const deleteWell = useCallback((id: string): void => {
    wellsStore.setAll(wellsStore.read().filter((w) => w.id !== id));
  }, []);

  return {
    wells,
    getWells,
    getWellById,
    addWell,
    createWell,
    updateWell,
    archiveWell,
    restoreWell,
    deleteWell,
  };
}

export default useWellData;
